'use client'

import { NewsFavicon } from '@/components/NewsFavicon'
import { faviconUrl } from '@/components/ui/SourceMeta'
import { trackOutboundArticle } from '@/lib/tracking'
import { useEffect, useRef, useState } from 'react'

// The photos under a story's headline (2026-10-23, after Henry Blodget's
// note to make images more involved): a horizontal slider of collage
// slides, each a big photo and two smaller ones, every photo from a
// different outlet, credited and linked to its article. Swipe on a phone,
// arrows on a computer. The photos come from utils/storyPhotos
// (storyPhotoPool: no wire photos, center/left/right taking turns); only
// the ones that load are used, so a slide never has a hole. CSS
// backgrounds, not <img>, so Google Images doesn't index outlets' photos.
// (A single collage and photos inside the both-sides text were tried and
// dropped the same day.)

// The pool's photos that load, in pool order, decided ONCE: when every probe
// has answered or after WAIT_MS, whichever comes first. Showing photos as
// each loaded regrouped the slides two or three times in the first second,
// which read as a flicker and pushed the story down (2026-10-24). Until then
// `photos` is null and the slider holds its space with a placeholder.
const WAIT_MS = 1500

function useLoaded(pool) {
  const [photos, setPhotos] = useState(pool.length ? null : [])
  useEffect(() => {
    if (!pool.length) return
    const ok = new Set()
    let answered = 0
    let done = false
    const finish = () => {
      if (done) return
      done = true
      setPhotos(pool.filter((a) => ok.has(a.image)))
    }
    const probes = pool.map((a) => {
      const p = new window.Image()
      p.onload = () => {
        ok.add(a.image)
        if (++answered === pool.length) finish()
      }
      p.onerror = () => {
        if (++answered === pool.length) finish()
      }
      p.src = a.image
      return p
    })
    const timer = setTimeout(finish, WAIT_MS)
    return () => {
      done = true
      clearTimeout(timer)
      probes.forEach((p) => {
        p.onload = null
        p.onerror = null
      })
    }
  }, [pool])
  return photos
}

// A placeholder tile: the shimmer, with a faint stand-in for the outlet
// credit in the corner.
function SkeletonTile({ className = '', delay = '0ms' }) {
  return (
    <div
      className={`shimmer relative rounded-sm ${className}`}
      style={{ animationDelay: delay }}
    >
      <span className="absolute bottom-2 left-2.5 h-2 w-16 rounded-full bg-foreground/10" />
    </div>
  )
}

function Tile({ article, className = '' }) {
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        trackOutboundArticle(article, { surface: 'story_photo_slider' })
      }
      aria-label={`Photo from ${article.outlet.name}: ${article.title}`}
      className={`group relative block overflow-hidden rounded-sm bg-muted bg-cover bg-center ${className}`}
      style={{
        backgroundImage: `url("${String(article.image).replace(/"/g, '%22')}")`,
      }}
    >
      {/* A taller, softer shade under the credit so the name reads on
          light photos too (2026-10-23), plus a faint text shadow. */}
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-2.5 pb-2 pt-12 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
        <span className="inline-flex items-center gap-1.5 font-sans text-[11px] font-medium text-white">
          {/* The outlet's favicon beside its name (Sam, 2026-10-23). */}
          <NewsFavicon
            src={faviconUrl(article.outlet.slug)}
            alt=""
            faviconSize="xs"
            className="shrink-0"
          />
          {article.outlet.name}
          <span className="opacity-70 transition-opacity group-hover:opacity-100">
            &rarr;
          </span>
        </span>
      </span>
    </a>
  )
}

// One slide: a big photo with up to two stacked beside it (below it on a
// phone); a slide of one is a single wide photo.
function Slide({ photos }) {
  const [big, ...rest] = photos
  if (!rest.length) {
    return <Tile article={big} className="h-56 w-full sm:h-80" />
  }
  return (
    <div className="grid grid-cols-2 gap-1.5 sm:h-80 sm:grid-cols-3 sm:grid-rows-2">
      <Tile article={big} className="col-span-2 h-56 sm:row-span-2 sm:h-auto" />
      {rest.map((a) => (
        <Tile
          key={a.id}
          article={a}
          className={`h-32 sm:h-auto ${rest.length === 1 ? 'col-span-2 sm:col-span-1 sm:row-span-2' : ''}`}
        />
      ))}
    </div>
  )
}

export default function StorySlides({ pool = [], className = '' }) {
  const photos = useLoaded(pool)
  const track = useRef(null)
  const [at, setAt] = useState(0)

  // Still loading: the collage's shape in shimmering tiles, the same size
  // as a slide, so the story doesn't jump and the photos fade in where they
  // will sit.
  if (photos === null) {
    return (
      <div className={className} aria-hidden="true">
        <div className="grid grid-cols-2 gap-1.5 sm:h-80 sm:grid-cols-3 sm:grid-rows-2">
          <SkeletonTile className="col-span-2 h-56 sm:row-span-2 sm:h-auto" />
          <SkeletonTile className="h-32 sm:h-auto" delay="150ms" />
          <SkeletonTile className="h-32 sm:h-auto" delay="300ms" />
        </div>
        <div className="mt-2 h-1.5" />
      </div>
    )
  }

  const slides = []
  for (let i = 0; i < photos.length; i += 3) slides.push(photos.slice(i, i + 3))
  // A last slide of one photo, after full ones, reads as a leftover: drop it.
  if (slides.length > 1 && slides.at(-1).length === 1) slides.pop()
  if (!slides.length) return null

  const go = (i) => {
    const el = track.current
    if (!el) return
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }
  const onScroll = () => {
    const el = track.current
    if (el) setAt(Math.round(el.scrollLeft / el.clientWidth))
  }

  return (
    <div className={`relative animate-[fadein_0.35s_ease-out] ${className}`}>
      <div
        ref={track}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-roledescription="carousel"
        aria-label="Photos from the coverage"
      >
        {slides.map((s, i) => (
          <div
            key={i}
            className="w-full shrink-0 snap-start"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
          >
            <Slide photos={s} />
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(Math.max(0, at - 1))}
            disabled={at === 0}
            aria-label="Previous photos"
            className="absolute left-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white transition-opacity hover:bg-black/70 disabled:opacity-0 sm:flex"
          >
            &larr;
          </button>
          <button
            type="button"
            onClick={() => go(Math.min(slides.length - 1, at + 1))}
            disabled={at === slides.length - 1}
            aria-label="More photos"
            className="absolute right-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white transition-opacity hover:bg-black/70 disabled:opacity-0 sm:flex"
          >
            &rarr;
          </button>
          <div className="mt-2 flex justify-center gap-1.5" aria-hidden="true">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                tabIndex={-1}
                onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${i === at ? 'w-4 bg-foreground' : 'w-1.5 bg-foreground/30'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
